import {
  IAgenteRespostaResult,
  IConversaView,
  IProposta,
  ISaudeResult,
  IScreenContext,
} from '@/domain/agente/interfaces/agente';
import { agenteRepository } from '@/domain/agente/repositories/agenteRepository';
import { logger } from '@/infrastructure/persistence/managers/LoggerManager';
import { createStore } from '@/lib/zustand';

interface AgenteState {
  conversa: IConversaView;
  saude: ISaudeResult | null;
  /** Painel lateral visivel. */
  painelAberto: boolean;
  loading: boolean;
  loadingConfirmar: string | null;
  erro: string | null;

  abrirPainel: () => void;
  fecharPainel: () => void;
  togglePainel: () => void;

  enviar: (texto: string, contexto: IScreenContext) => Promise<IAgenteRespostaResult | null>;
  confirmar: (propostaId: string) => Promise<void>;
  limpar: () => Promise<void>;
  carregarConversa: () => Promise<void>;
  carregarSaude: () => Promise<void>;
}

const conversaVazia: IConversaView = { mensagens: [], propostasPendentes: [] };

export const agenteStore = createStore<AgenteState>((set, get) => ({
  conversa: conversaVazia,
  saude: null,
  painelAberto: false,
  loading: false,
  loadingConfirmar: null,
  erro: null,

  abrirPainel: () => {
    set({ painelAberto: true });
  },

  fecharPainel: () => {
    set({ painelAberto: false });
  },

  togglePainel: () => {
    set((s) => ({ painelAberto: !s.painelAberto }));
  },

  enviar: async (texto, contexto) => {
    set({ loading: true, erro: null });
    try {
      const resultado = await agenteRepository.mensagem(texto, contexto);
      const conversa = await agenteRepository.getConversa();
      set({ conversa });
      return resultado;
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      logger.error('[agenteStore] enviar failed', error);
      set({ erro: msg });
      return null;
    } finally {
      set({ loading: false });
    }
  },

  confirmar: async (propostaId) => {
    set({ loadingConfirmar: propostaId, erro: null });
    try {
      await agenteRepository.confirmar(propostaId);
      const conversa = await agenteRepository.getConversa();
      set({ conversa });
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      logger.error('[agenteStore] confirmar failed', error);
      set({ erro: msg });
    } finally {
      set({ loadingConfirmar: null });
    }
  },

  limpar: async () => {
    try {
      await agenteRepository.limparConversa();
      set({ conversa: conversaVazia });
    } catch (error) {
      logger.error('[agenteStore] limpar failed', error);
    }
  },

  carregarConversa: async () => {
    if (get().loading) {
      return;
    }
    try {
      const conversa = await agenteRepository.getConversa();
      set({ conversa });
    } catch (error) {
      logger.error('[agenteStore] carregarConversa failed', error);
    }
  },

  carregarSaude: async () => {
    try {
      const saude = await agenteRepository.saude();
      set({ saude });
    } catch (error) {
      logger.error('[agenteStore] carregarSaude failed', error);
    }
  },
}));
